import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Component to handle Google OAuth callback
 * Extracts the token from URL and performs auto-login
 */
const GoogleCallback = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { validateToken } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get token from URL query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const role = params.get('role');
        
        if (!token) {
          throw new Error('No authentication token received');
        }
        
        // Store token in localStorage
        localStorage.setItem('token', token);
        
        // Validate token to get user info and complete login
        const user = await validateToken();
        
        // Redirect based on role
        if (role === 'legal_professional' || user?.role === 'legal_professional') {
          navigate('/search'); // Legal professional dashboard
        } else {
          navigate('/search'); // Regular user dashboard
        }
      } catch (error) {
        console.error('Google authentication error:', error);
        setError('Authentication failed. Please try again.');
        setTimeout(() => {
          navigate('/signin');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };
    
    handleCallback();
  }, [location, navigate, validateToken]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%)' }}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
        <p className="mt-4 text-xl text-white">Completing authentication...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'linear-gradient(180deg, #301555 0%, #411C73 50%, #1E0B38 100%)' }}>
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-8 max-w-md w-full border border-white/20">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-2 text-xl font-semibold text-white">Authentication Error</h2>
            <p className="mt-2 text-gray-300">{error}</p>
            <p className="mt-4 text-gray-400">Redirecting to login page...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default GoogleCallback; 